import React from 'react';
import { Alert, Box } from '@mui/material';
import SafeMathRenderer from './SafeMathRenderer';
import DOMPurify from 'dompurify';

interface UnifiedRendererProps {
  /** 内容（HTML、LaTeX、纯文本都支持） */
  content: string;
  /** 渲染模式 */
  mode?: 'auto' | 'html' | 'formula' | 'text';
  /** 是否行内显示 */
  inline?: boolean;
  /** 内容截断长度（用于列表显示） */
  maxLength?: number;
  /** CSS类名 */
  className?: string;
  /** 内联样式 */
  style?: React.CSSProperties;
  /** 错误回调 */
  onError?: (error: string) => void;
  /** 是否显示错误信息 */
  showError?: boolean;
}

/**
 * 统一渲染器 - 重构简化版
 * 
 * 特点：
 * - 使用SimpleMathRenderer替代复杂的MathJax处理
 * - 移除所有复杂的DOM验证和错误处理
 * - 简化生命周期管理
 * - 彻底解决nextSibling错误
 */
const UnifiedRenderer: React.FC<UnifiedRendererProps> = ({
  content,
  mode = 'auto',
  inline = false,
  maxLength,
  className = '',
  style = {},
  onError,
  showError = false
}) => {

  // 内容有效性检查
  if (!content || typeof content !== 'string' || content.trim() === '') {
    return null;
  }

  // 内容截断函数
  const truncateContent = (content: string, maxLength: number): string => {
    if (content.length <= maxLength) return content;
    
    let truncated = content.substring(0, maxLength);
    
    // 智能截断：避免截断公式标记
    const formulaStart = truncated.lastIndexOf('\\(');
    const formulaEnd = truncated.lastIndexOf('\\)');
    
    if (formulaStart > formulaEnd && formulaStart !== -1) {
      truncated = content.substring(0, formulaStart);
    }
    
    // 避免截断HTML标签
    const tagStart = truncated.lastIndexOf('<');
    const tagEnd = truncated.lastIndexOf('>');
    
    if (tagStart > tagEnd && tagStart !== -1) {
      truncated = content.substring(0, tagStart);
    }
    
    return truncated + '...';
  };

  // HTML内容清理和增强
  const sanitizeHtml = (html: string): string => {
    try {
      // 预处理：增强audio标签
      const enhancedHtml = enhanceAudioTags(html);
      
      return DOMPurify.sanitize(enhancedHtml, {
        ALLOWED_TAGS: [
          'p', 'div', 'span', 'br', 'strong', 'em', 'u', 'sub', 'sup',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li',
          'img', 'audio', 'video', 'source',
          'table', 'tr', 'td', 'th', 'thead', 'tbody'
        ],
        ALLOWED_ATTR: [
          'src', 'alt', 'title', 'href', 'target',
          'style', 'class', 'id',
          'controls', 'autoplay', 'loop', 'type'
        ],
        KEEP_CONTENT: true
      });
    } catch (error) {
      console.warn('HTML清理失败:', error);
      return html;
    }
  };

  // 增强audio标签，自动添加type属性和fallback
  const enhanceAudioTags = (html: string): string => {
    // 处理没有type属性的source标签
    let enhanced = html.replace(
      /<source\s+src=["']([^"']+)["'](?!\s+type=)/g,
      (match, src) => {
        const type = getAudioMimeType(src);
        return `<source src="${src}" type="${type}"`;
      }
    );

    // 为没有fallback文本的audio标签添加fallback
    enhanced = enhanced.replace(
      /<audio([^>]*?)><source([^>]*?)><\/audio>/g,
      '<audio$1><source$2>您的浏览器不支持音频播放。</audio>'
    );

    return enhanced;
  };

  // 根据文件扩展名或URL判断音频MIME类型
  const getAudioMimeType = (src: string): string => {
    const url = src.toLowerCase();
    
    // 根据文件扩展名判断
    if (url.includes('.mp3')) return 'audio/mpeg';
    if (url.includes('.wav')) return 'audio/wav';
    if (url.includes('.ogg')) return 'audio/ogg';
    if (url.includes('.m4a')) return 'audio/mp4';
    if (url.includes('.aac')) return 'audio/aac';
    
    // 根据URL路径判断（七牛云的audio路径）
    if (url.includes('/audio/')) return 'audio/mpeg';
    
    // 默认为MPEG格式
    return 'audio/mpeg';
  };

  // 公式预处理
  const preprocessFormula = (formula: string): string => {
    let processed = formula.trim();
    
    // 检测是否为化学公式
    const isChemistry = detectChemistryFormula(processed);
    
    if (isChemistry && !processed.includes('\\ce{') && !processed.includes('\\mathrm{')) {
      // 自动包装化学公式
      processed = `\\ce{${processed}}`;
    }
    
    return processed;
  };

  // 化学公式检测
  const detectChemistryFormula = (formula: string): boolean => {
    // 排除单字符和纯数学表达式
    if (/^[A-Za-z0-9]$/.test(formula.trim())) return false;
    if (/^[\d+\-*/().\s=<>]+$/.test(formula)) return false;
    
    // 明确的化学标记
    if (/\\ce\{|\\pu\{|\\mathrm\{/.test(formula)) return true;
    
    // 化学特征检测
    const chemPatterns = [
      /^[A-Z][a-z]?\d+$/,                    // H2O, CO2
      /^[A-Z][a-z]?\d*\([A-Z][a-z]?\d*\)\d+$/, // Ca(OH)2
      /[A-Z][a-z]?\d+\s*[+\-→]\s*[A-Z][a-z]?\d+/, // 化学反应
      /\^[+-]\d*$/                           // 离子电荷
    ];
    
    return chemPatterns.some(pattern => pattern.test(formula));
  };

  // 内容截断处理
  const processedContent = maxLength ? truncateContent(content, maxLength) : content;

  // 智能检测内容类型
  const detectContentType = (content: string): 'html' | 'formula' | 'text' => {
    // 包含HTML标签
    if (/<[^>]+>/.test(content)) return 'html';

    // 包含LaTeX标记
    if (/\$|\\\(|\\\[|\\ce\{|\\mathrm\{|\\pu\{/.test(content)) return 'formula';

    // 纯文本
    return 'text';
  };

  // 获取实际渲染模式
  const actualMode = mode === 'auto' ? detectContentType(processedContent) : mode;

  // 渲染内容
  const renderContent = () => {
    try {
      switch (actualMode) {
        case 'html':
          // HTML内容渲染
          const cleanHtml = sanitizeHtml(processedContent);
          return (
            <SafeMathRenderer
              content={cleanHtml}
              inline={inline}
              onError={onError}
            />
          );

        case 'formula':
          // 纯公式渲染
          const processedFormula = preprocessFormula(processedContent);
          return (
            <SafeMathRenderer
              content={processedFormula}
              inline={inline}
              onError={onError}
            />
          );

        case 'text':
        default:
          // 纯文本渲染
          return (
            <span style={{ 
              lineHeight: '1.6',
              fontSize: '14px',
              color: '#333'
            }}>
              {processedContent}
            </span>
          );
      }
    } catch (error) {
      const errorMessage = `渲染失败: ${error instanceof Error ? error.message : String(error)}`;
      console.error('UnifiedRenderer错误:', error);
      
      if (onError) onError(errorMessage);
      
      if (showError) {
        return (
          <Alert severity="error">
            {errorMessage}
          </Alert>
        );
      }
      
      // 降级显示原始内容
      return (
        <span style={{ color: '#666', fontStyle: 'italic' }}>
          {content}
        </span>
      );
    }
  };

  return (
    <Box
      component={inline ? 'span' : 'div'}
      className={`unified-renderer ${className}`}
      style={{
        display: inline ? 'inline' : 'block',
        ...style
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default UnifiedRenderer;